import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Power, KeyRound, Trash2, Search } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Breadcrumbs from "@/components/admin/Breadcrumbs";
import { showPasswordToast } from "@/components/admin/PasswordToast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatApiError } from "@/lib/errors";

const STATUS_FILTERS = ["all", "active", "disabled"];

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function UserFormDialog({ open, onOpenChange, user, roles, apps, onSave }) {
  const isEdit = !!user;
  const [form, setForm] = useState({
    email: "",
    full_name: "",
    role: "operations",
    department: "",
    app_access: [],
    all_apps: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (user) {
      setForm({
        email: user.email,
        full_name: user.full_name,
        role: user.role,
        department: user.department || "",
        app_access: (user.app_access || []).filter((x) => x !== "*"),
        all_apps: (user.app_access || []).includes("*"),
      });
    } else {
      setForm({
        email: "",
        full_name: "",
        role: roles[0]?.name || "operations",
        department: "",
        app_access: [],
        all_apps: false,
      });
    }
  }, [open, user, roles]);

  const toggleApp = (id) => {
    setForm((f) => ({
      ...f,
      app_access: f.app_access.includes(id)
        ? f.app_access.filter((x) => x !== id)
        : [...f.app_access, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        email: form.email.trim().toLowerCase(),
        full_name: form.full_name.trim(),
        role: form.role,
        department: form.department.trim() || null,
        app_access: form.all_apps ? ["*"] : form.app_access,
      };
      if (isEdit) {
        const { email: _drop, ...rest } = payload;
        void _drop;
        const { data } = await api.patch(`/admin/users/${user.id}`, rest);
        toast.success("User updated");
        onSave(data);
      } else {
        const { data } = await api.post("/admin/users", payload);
        showPasswordToast({
          label: `Temp password for ${data.user.email}`,
          password: data.temp_password,
        });
        onSave(data.user);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err) || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit user" : "Create user"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the user's role, department and app access."
              : "A temporary password will be shown once on the next screen."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Full name</Label>
            <Input
              required
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              data-testid="user-form-full-name"
            />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              type="email"
              required
              disabled={isEdit}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="user-form-email"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm({ ...form, role: v })}
              >
                <SelectTrigger data-testid="user-form-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r.name} value={r.name}>
                      {r.display_name || r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Department</Label>
              <Input
                value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
                placeholder="Optional"
                data-testid="user-form-department"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>App access</Label>
            <label className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm">
              <Checkbox
                checked={form.all_apps}
                onCheckedChange={(v) =>
                  setForm({ ...form, all_apps: Boolean(v) })
                }
                data-testid="user-form-all-apps"
              />
              All apps (admin override)
            </label>
            {!form.all_apps && (
              <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2">
                {apps.length === 0 && (
                  <div className="py-4 text-center text-xs text-slate-500">
                    No apps registered yet.
                  </div>
                )}
                {apps.map((a) => (
                  <label
                    key={a.id}
                    className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50"
                  >
                    <Checkbox
                      checked={form.app_access.includes(a.id)}
                      onCheckedChange={() => toggleApp(a.id)}
                      data-testid={`user-form-app-${a.id}`}
                    />
                    <span>{a.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="user-form-submit"
              className="bg-cargodash-primary-600 hover:bg-cargodash-primary-700"
            >
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Create user"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmAction({ open, onOpenChange, title, description, actionLabel, destructive, onConfirm }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            data-testid="confirm-dialog-action"
            className={destructive ? "bg-cargodash-alert-600 hover:bg-cargodash-alert-600/90" : undefined}
          >
            {actionLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function AdminUsers() {
  const { user: me, refresh } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, r, a] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/roles"),
        api.get("/admin/apps"),
      ]);
      setUsers(u.data);
      setRoles(r.data);
      setApps(a.data);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (statusFilter !== "all" && u.status !== statusFilter) return false;
      if (!q) return true;
      return (
        u.full_name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
      );
    });
  }, [users, search, roleFilter, statusFilter]);

  const afterSave = async () => {
    await load();
    await refresh();
  };

  const doToggleStatus = async (u) => {
    try {
      const action = u.status === "active" ? "disable" : "enable";
      await api.post(`/admin/users/${u.id}/${action}`);
      toast.success(`User ${action}d`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Failed");
    }
  };

  const doReset = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      showPasswordToast({
        label: `New temp password for ${u.email}`,
        password: data.temp_password,
      });
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Failed");
    }
  };

  const doDelete = async (u) => {
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Failed");
    }
  };

  const appNamesFor = (ids) => {
    if (ids.includes("*")) return "All apps";
    const names = ids
      .map((id) => apps.find((a) => a.id === id)?.name)
      .filter(Boolean);
    return names.length ? names.join(", ") : "None";
  };

  return (
    <div data-testid="admin-users" className="mx-auto w-full max-w-6xl">
      <Breadcrumbs />
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900">
            Users
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {users.length} user{users.length === 1 ? "" : "s"} · {users.filter((u) => u.status === "active").length} active
          </p>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          data-testid="users-create-btn"
          className="gap-2 bg-cargodash-primary-600 hover:bg-cargodash-primary-700"
        >
          <Plus className="h-4 w-4" />
          New user
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email"
            className="pl-9"
            data-testid="users-search"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[180px]" data-testid="users-filter-role">
            <SelectValue placeholder="All roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {roles.map((r) => (
              <SelectItem key={r.name} value={r.name}>
                {r.display_name || r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="users-filter-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_FILTERS.map((s) => (
              <SelectItem key={s} value={s}>
                {s[0].toUpperCase() + s.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-4">Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Apps</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last login</TableHead>
              <TableHead className="pr-4 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} className="py-10 text-center text-sm text-slate-500">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-10 text-center text-sm text-slate-500">
                  No users match the filters.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((u) => {
              const isSelf = u.id === me?.id;
              return (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="pl-4 font-medium text-slate-900">
                    {u.full_name}
                  </TableCell>
                  <TableCell className="text-slate-600">{u.email}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="font-mono text-[10px]">
                      {u.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span
                      title={appNamesFor(u.app_access || [])}
                      className="text-xs text-slate-600"
                    >
                      {u.app_access?.includes("*")
                        ? "All"
                        : (u.app_access || []).length}
                    </span>
                  </TableCell>
                  <TableCell>
                    {u.status === "active" ? (
                      <Badge className="bg-cargodash-primary-50 text-cargodash-primary-700 hover:bg-cargodash-primary-50">
                        Active
                      </Badge>
                    ) : (
                      <Badge className="bg-cargodash-alert-600/10 text-cargodash-alert-600 hover:bg-cargodash-alert-600/10">
                        Disabled
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {formatDate(u.last_login_at)}
                  </TableCell>
                  <TableCell className="pr-4 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => {
                          setEditing(u);
                          setFormOpen(true);
                        }}
                        data-testid={`user-edit-${u.id}`}
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        disabled={isSelf}
                        onClick={() =>
                          setConfirm({
                            type: u.status === "active" ? "disable" : "enable",
                            user: u,
                          })
                        }
                        data-testid={`user-toggle-${u.id}`}
                        title={u.status === "active" ? "Disable" : "Enable"}
                      >
                        <Power className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setConfirm({ type: "reset", user: u })}
                        data-testid={`user-reset-${u.id}`}
                        title="Reset password"
                      >
                        <KeyRound className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        disabled={isSelf}
                        onClick={() => setConfirm({ type: "delete", user: u })}
                        data-testid={`user-delete-${u.id}`}
                        title="Delete"
                        className="text-cargodash-alert-600 hover:bg-cargodash-alert-600/10 hover:text-cargodash-alert-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <UserFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        user={editing}
        roles={roles}
        apps={apps}
        onSave={afterSave}
      />

      <ConfirmAction
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={
          confirm?.type === "disable"
            ? `Disable ${confirm.user.full_name}?`
            : confirm?.type === "enable"
            ? `Enable ${confirm.user.full_name}?`
            : confirm?.type === "reset"
            ? `Reset password for ${confirm.user.full_name}?`
            : confirm?.type === "delete"
            ? `Delete ${confirm.user.full_name}?`
            : ""
        }
        description={
          confirm?.type === "disable"
            ? "The user will be logged out of every device immediately and won't be able to sign in until re-enabled."
            : confirm?.type === "enable"
            ? "The user will be able to sign in again with their existing password."
            : confirm?.type === "reset"
            ? "A new temporary password will be generated and shown once. All active sessions for this user will be revoked."
            : confirm?.type === "delete"
            ? "This is permanent. The account and all its sessions will be removed."
            : ""
        }
        actionLabel={
          confirm?.type === "disable"
            ? "Disable user"
            : confirm?.type === "enable"
            ? "Enable user"
            : confirm?.type === "reset"
            ? "Reset & show password"
            : confirm?.type === "delete"
            ? "Delete user"
            : "Confirm"
        }
        destructive={["disable", "delete"].includes(confirm?.type)}
        onConfirm={async () => {
          const c = confirm;
          setConfirm(null);
          if (!c) return;
          if (c.type === "disable" || c.type === "enable") await doToggleStatus(c.user);
          else if (c.type === "reset") await doReset(c.user);
          else if (c.type === "delete") await doDelete(c.user);
        }}
      />
    </div>
  );
}
